/**
 * React Query (TanStack Query) configuration for SendGuard
 */

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { useStore } from '@/store';
import { apiEndpoints } from '@/lib/api';

/**
 * Create the query client with global error handling and cache configuration
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: 5 minutes (data is considered fresh for 5 minutes)
      staleTime: 5 * 60 * 1000,
      // Cache time: 10 minutes (data stays in cache for 10 minutes after becoming inactive)
      gcTime: 10 * 60 * 1000,
      // Retry failed requests 3 times with exponential backoff
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors except 429 (rate limit)
        if (error?.status >= 400 && error?.status < 500 && error?.status !== 429) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus in production only
      refetchOnWindowFocus: process.env.NODE_ENV === 'production',
      // Don't refetch on reconnect by default
      refetchOnReconnect: 'always',
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      retryDelay: 1000,
    },
  },
  queryCache: new QueryCache({
    onError: (error: any, query) => {
      // Global query error handler
      const state = useStore.getState();

      // Don't show errors for background refetches
      if (query.state.data !== undefined) {
        return;
      }

      // Handle specific error types
      if (error?.status === 401) {
        // Auth errors are handled by the API client
        return;
      }

      if (error?.status === 403) {
        state.addNotification({
          type: 'error',
          title: 'Permission Denied',
          message: 'You do not have permission to perform this action.',
          duration: 5000,
        });
        return;
      }

      if (error?.status === 404) {
        // The currentOrganization query returns 404 for new users who haven't
        // created an org yet — this is expected and handled by the onboarding flow.
        const isOrgQuery =
          query.queryKey.includes('organizations') && query.queryKey.includes('current');
        if (!isOrgQuery) {
          state.addNotification({
            type: 'error',
            title: 'Not Found',
            message: 'The requested resource could not be found.',
            duration: 5000,
          });
        }
        return;
      }

      if (error?.status >= 500) {
        state.addNotification({
          type: 'error',
          title: 'Server Error',
          message: 'An unexpected error occurred. Please try again later.',
          duration: 5000,
        });
        return;
      }

      // Generic error
      state.addNotification({
        type: 'error',
        title: 'Error',
        message: error?.message || 'An unexpected error occurred.',
        duration: 5000,
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: any, _variables, _context, mutation) => {
      // Global mutation error handler
      const state = useStore.getState();

      // Skip if mutation has custom error handling
      if (mutation.options.onError) {
        return;
      }

      // Handle specific error types
      if (error?.status === 401) {
        // Auth errors are handled by the API client
        return;
      }

      if (error?.status === 403) {
        state.addNotification({
          type: 'error',
          title: 'Permission Denied',
          message: 'You do not have permission to perform this action.',
          duration: 5000,
        });
        return;
      }

      if (error?.status === 422) {
        state.addNotification({
          type: 'error',
          title: 'Validation Error',
          message: error?.message || 'Please check your input and try again.',
          duration: 5000,
        });
        return;
      }

      if (error?.status >= 500) {
        state.addNotification({
          type: 'error',
          title: 'Server Error',
          message: 'An unexpected error occurred. Please try again later.',
          duration: 5000,
        });
        return;
      }

      // Generic error
      state.addNotification({
        type: 'error',
        title: 'Error',
        message: error?.message || 'An unexpected error occurred.',
        duration: 5000,
      });
    },
  }),
});

/**
 * Query key factory for consistent query key generation
 * Follows the recommended pattern from TanStack Query docs
 */
export const queryKeys = {
  all: ['sendguard'] as const,

  // Auth queries
  auth: () => [...queryKeys.all, 'auth'] as const,
  session: () => [...queryKeys.auth(), 'session'] as const,

  // Organization queries
  organizations: () => [...queryKeys.all, 'organizations'] as const,
  organization: (id: string) => [...queryKeys.organizations(), id] as const,
  currentOrganization: () => [...queryKeys.organizations(), 'current'] as const,

  // Workspace queries
  workspaces: () => [...queryKeys.all, 'workspaces'] as const,
  workspace: (id: string) => [...queryKeys.workspaces(), id] as const,
  workspaceMembers: (id: string) => [...queryKeys.workspace(id), 'members'] as const,
  workspaceBrandDomains: (id: string) => [...queryKeys.workspace(id), 'brand-domains'] as const,
  dashboardMetrics: (workspaceId: string) =>
    [...queryKeys.workspace(workspaceId), 'dashboard', 'metrics'] as const,
  workspacesByOrg: (orgId: string) => [...queryKeys.organizations(), orgId, 'workspaces'] as const,

  // Campaign/Submission queries
  campaigns: (workspaceId: string) => [...queryKeys.workspace(workspaceId), 'campaigns'] as const,
  campaign: (workspaceId: string, campaignId: string) =>
    [...queryKeys.campaigns(workspaceId), campaignId] as const,

  // Submission queries (using campaigns endpoint)
  submissions: (workspaceId: string) => [...queryKeys.campaigns(workspaceId)] as const,
  submission: (workspaceId: string, submissionId: string) =>
    [...queryKeys.campaign(workspaceId, submissionId)] as const,

  // User queries
  users: () => [...queryKeys.all, 'users'] as const,
  user: (id: string) => [...queryKeys.users(), id] as const,
} as const;

/**
 * Helper to invalidate related queries after mutations
 */
export const invalidateQueries = {
  // Invalidate all workspace-related queries
  workspace: (workspaceId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.workspace(workspaceId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.workspaces() });
  },

  // Invalidate organization queries
  organization: (orgId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.organization(orgId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.currentOrganization() });
    queryClient.invalidateQueries({ queryKey: queryKeys.workspacesByOrg(orgId) });
  },

  // Invalidate campaign/submission queries
  campaigns: (workspaceId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.campaigns(workspaceId) });
  },

  // Invalidate specific campaign
  campaign: (workspaceId: string, campaignId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.campaign(workspaceId, campaignId) });
  },
};

/**
 * Custom hook for prefetching data on navigation intent (hover, focus)
 * This enables instant page loads by warming the cache before navigation
 *
 * IMPORTANT: Each prefetch must include a queryFn, otherwise React Query v5
 * will throw "Missing queryFn" errors when the cache is empty.
 */
export const usePrefetch = () => {
  const prefetchCampaigns = (workspaceId: string) => {
    if (!workspaceId) return;
    queryClient.prefetchQuery({
      queryKey: queryKeys.campaigns(workspaceId),
      queryFn: () => apiEndpoints.submissions.list(workspaceId, { limit: 20 }),
      staleTime: 30 * 1000, // Match hook's 30-second stale time
    });
  };

  const prefetchDashboardMetrics = (workspaceId: string) => {
    if (!workspaceId) return;
    queryClient.prefetchQuery({
      queryKey: queryKeys.dashboardMetrics(workspaceId),
      queryFn: () => apiEndpoints.workspaces.getDashboardMetrics(workspaceId),
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    });
  };

  /**
   * Prefetch all data needed for the dashboard page
   */
  const prefetchDashboard = (workspaceId: string) => {
    if (!workspaceId) return;
    prefetchDashboardMetrics(workspaceId);
    prefetchCampaigns(workspaceId); // For recent submissions
  };

  /**
   * Prefetch all data needed for the submissions page
   */
  const prefetchSubmissions = (workspaceId: string) => {
    if (!workspaceId) return;
    prefetchCampaigns(workspaceId);
  };

  return {
    prefetchCampaigns,
    prefetchDashboardMetrics,
    prefetchDashboard,
    prefetchSubmissions,
  };
};
